/* ────────────────────────────────────────────────────────────────────
   /read/:slug — Faitharama magazine reader.
   Renders each PDF page as an image so the whole page fits the screen
   regardless of device. Prev/Next + scroll, no browser PDF viewer quirks.
   ──────────────────────────────────────────────────────────────────── */
import { useParams, Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";

const API = process.env.REACT_APP_BACKEND_URL;

const BOOKS = {
  "the-sleep-solution-guide": {
    title: "The Sleep Solution Guide",
    pdf:   `${API}/api/uploads/pdfs/the-sleep-solution-guide.pdf`,
    previewBase: `${API}/api/uploads/book-previews/the-sleep-solution-guide`,
    pages: 8,
  },
  "sleep-well-after-60": {
    title: "Sleep Well After 60",
    pdf:   `${API}/api/uploads/pdfs/sleep-well-after-60.pdf`,
    previewBase: `${API}/api/uploads/book-previews/sleep-well`,
    pages: 22,
  },
  "lose-10-pounds-after-60": {
    title: "Lose 10 Pounds After 60",
    pdf:   `${API}/api/uploads/pdfs/lose-10-pounds-after-60.pdf`,
    previewBase: `${API}/api/uploads/book-previews/lose-10-pounds`,
    pages: 24,
  },
  "the-sugar-reset": {
    title: "The Sugar Reset",
    pdf:   `${API}/api/uploads/pdfs/the-sugar-reset.pdf`,
    previewBase: `${API}/api/uploads/book-previews/sugar-reset`,
    pages: 26,
  },
  "10-minutes-a-day-treadmill": {
    title: "10 Minutes A Day: How A Treadmill Changed My Life After 60",
    pdf:   `${API}/api/uploads/pdfs/10-minutes-a-day-treadmill.pdf`,
    previewBase: `${API}/api/uploads/book-previews/treadmill`,
    pages: 32,
  },
};

const padPage = (n) => String(n).padStart(2, "0");

export default function EpubReaderPage() {
  const { slug } = useParams();
  const book = BOOKS[slug];

  if (!book) {
    return (
      <div className="min-h-screen bg-white">
        <SiteNav />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold">Book not found</h1>
          <Link to="/market" className="text-blue-600 underline mt-4 inline-block">Back to Market</Link>
        </div>
      </div>
    );
  }

  // For older books we may not have preview images yet; fall back to PDF iframe.
  const hasPreviews = !!book.previewBase && book.pages > 0;

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F9FC]" data-testid="epub-reader-page">
      <SiteNav />
      <div className="max-w-6xl w-full mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif-display text-lg md:text-xl uppercase truncate" style={{ color: "#0F172A" }}>
          📖 {book.title}
        </h1>
        <div className="flex items-center gap-3">
          <a href={book.pdf} download
             data-testid="reader-download"
             className="px-3 py-2 rounded-md font-bold text-white" style={{ background: "#2E9F3A" }}>
            Download
          </a>
          <Link to="/market" data-testid="back-to-market"
                className="text-sm font-bold uppercase tracking-wider"
                style={{ color: "#2E9F3A" }}>← Back To Market</Link>
        </div>
      </div>

      <div className="flex-1 w-full mx-auto px-2 pb-6 flex items-start justify-center" style={{ maxWidth: 1100 }}>
        <iframe
          src={`${book.pdf}?v=${Date.now()}#view=Fit&toolbar=1`}
          title={book.title}
          className="w-full rounded-2xl shadow-lg border bg-white"
          style={{ height: "calc(100vh - 180px)" }}
          data-testid="reader-page-image"
        />
      </div>

      {hasPreviews && (
        <div className="text-center text-xs text-gray-500 pb-4">
          Use the PDF toolbar to flip pages, zoom, or search.  ·  Hit Download for the full file.
        </div>
      )}
    </div>
  );
}
